import styled, { css } from 'styled-components';
import colors from '@designSystem/colors';
import { baseTyping } from '@designSystem/typings';
import Button from '@root/src/app/designSystem/buttons';
import { ErrorLabel } from '@designSystem/input/styled';

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${colors.white};

  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05),
    0px 12px 48px rgba(0, 0, 0, 0.25);
  border-radius: 10px;
`;

const BaseRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  width: calc(100% - 80px);
  margin: 30px 40px 0px 40px;

  & h2 {
    margin: 0px;
  }
`;

export const Row = styled(BaseRow)`
  align-items: flex-start;
  position: relative;
`;

export const RowWithError = styled.div`
  width: 100%;
`;

interface isMobileViewI {
  isMobile?: boolean;
}

export const WrapActionRow = styled.div<isMobileViewI>`
  width: calc(100% - 80px);
  display: flex;
  justify-content: space-between;
  margin: ${(props) =>
    props.isMobile ? '30px 40px 0 40px' : '30px 40px 40px 40px'};
`;

export const ActionRow = styled(BaseRow)<isMobileViewI>`
  margin: ${(props) => !props.isMobile && '0'};
  width: ${(props) => (props.isMobile ? 'calc(100% - 80px)' : 'unset')};
  justify-content: ${(props) => props.isMobile && 'flex-end'};

  > button {
    margin-left: 16px;
  }
`;

export const HeaderRow = styled(BaseRow)`
  width: calc(100% - 80px);
  justify-content: space-between;
`;

export const MobileContainer = styled(Container)`
  width: 100%;
  position: relative;

  border-radius: 10px 10px 0px 0px;
  box-shadow: none;
`;

export const ResultOption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  & > span {
    margin-left: 10px;
  }

  & > svg {
    width: 14px;
    height: 14px;
  }
`;

export const GreenSpan = styled.span`
  color: ${colors.green};
`;

export const ResultDescriptionContainer = styled.div`
  ${baseTyping}
  color: ${colors.black};
  font-size: 16px;
  line-height: 24px;
  margin-left: 40px;
`;

export const ResultThirdPageText = styled.p`
  ${baseTyping}
  font-family: Arial, sans-serif;
  color: ${colors.black};
  font-size: 16px;
  line-height: 24px;
  margin: 40px 0 0 40px;
`;

interface StepPropsI {
  page: number;
}

export const StepperContainer = styled.div<StepPropsI>`
  padding-top: 4px;
  display: flex;
  flex-direction: row;
  & > div:first-child {
    width: ${(props) => (props.page === 1 ? '40px' : '4px')};
    background-color: ${(props) =>
      props.page >= 1 ? colors.green : colors.grey};
  }
  & > div:nth-child(2) {
    margin: 0 8px 0 8px;
    width: ${(props) => (props.page === 2 ? '40px' : '4px')};
    background-color: ${(props) =>
      props.page >= 2 ? colors.green : colors.grey};
  }
  & > div:last-child {
    margin: 0 16px 0 0;
    width: ${(props) => (props.page === 3 ? '40px' : '4px')};
    background-color: ${(props) =>
      props.page >= 3 ? colors.green : colors.grey};
  }
`;

export const MethodText = styled.textarea.attrs({ rows: 4 })`
  resize: none;
  margin: 24px 40px 0 40px;
  width: calc(100% - 80px);
  padding: 8px 16px;
  border-radius: 8px;
  border: 1.5px solid ${colors.grey};
  background-color: ${colors.lightGrey};

  color: ${colors.black};
  font-family: Arial;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
`;

export const Stepper = styled.div`
  height: 4px;
  border-radius: 100px;
  min-width: 4px;
  max-width: 40px;
`;

export const StepText = styled.p`
  ${baseTyping}
  font-family: Arial, sans-serif;
  color: ${colors.grey};
  font-size: 12px;
  line-height: 13px;
  margin-bottom: 0;
  padding-top: 4px;

  @media (max-width: 991px) {
    padding-top: 0;
  }
`;

export const ImageContainer = styled.div``;

export const ResultImage = styled.img`
  height: auto;
  width: calc(100% - 80px);

  border-radius: 10px;
  margin: 36px 40px 0px 40px;

  ${ImageContainer}:hover & {
    filter: opacity(25%);
  }
`;

const desktop = css`
  ${ImageContainer}:hover & {
    display: flex;
    top: 210px;
    left: 184px;
    filter: opacity(100%);
  }
`;

const mobile = css`
  top: 270px;
  right: 15%;
`;

type IsMobileI = {
  isMobile?: boolean;
};

export const ActionsContainer = styled.div<IsMobileI>`
  position: absolute;
  display: ${(props) => (props.isMobile ? 'flex' : 'none')};
  ${({ isMobile }) => (isMobile ? mobile : desktop)}
`;

export const CustomErrorLabel = styled(ErrorLabel)`
  margin-top: 0px;
  margin-left: 40px;
`;

const mobileButton = css`
  width: 120px;
  padding: 8px 20px;
`;

export const ButtonWithMargin = styled(Button)<IsMobileI>`
  margin-left: 24px;
  ${({ isMobile }) => isMobile && mobileButton}
`;

interface PropsImageI {
  zoomImage: boolean;
}

const zoom = css`
  zoom: 2;
  transform-origin: left top;
  max-width: unset;
`;

export const ModalImage = styled.img<PropsImageI>`
  object-fit: contain;
  margin: 0 auto;
  max-width: 100%;
  max-height: calc(100vh - 20px);
  height: calc(100vh - 20px);
  cursor: ${(props) => (props.zoomImage ? 'zoom-out' : 'zoom-in')};
  ${({ zoomImage }) => zoomImage && zoom}
`;

export const ErrorMessage = styled.p`
  position: absolute;
  left: 15px;
  bottom: -25px;
  color: ${colors.red};
  font-size: 16px;
  font-family: 'futura-pt', sans-serif;

  @media (max-width: 430px) {
    bottom: -50px;
  }
`;
